import { useEffect, useRef, useState } from 'react';
import { Box, styled, TextField } from '@mui/material';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { DateTime } from 'luxon';

const PREFIX = 'CellEditableField';

const classes = {
  container: `${PREFIX}-container`,
  input: `${PREFIX}-input`,
};

type FieldType = 'text' | 'time';

type EditableFieldProps = {
  hasFocus: boolean;
  value: string;
  valueFormat?: string;
  defaultValue?: string;
  placeholder?: string;
  fieldType?: FieldType;
  align?: 'left' | 'center' | 'right';
  onChange?: (value: string | DateTime | null) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(1),
    '& input': {
      padding: theme.spacing(1),
      textAlign: 'center',
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
    '& fieldset': {
      display: 'block',
    },
  },
}));

/*
 * CellEditableField is a component that renders a text field that is editable.
 * To be used within the DataGridPro component.
 *
 * @param {boolean} hasFocus - Whether the cell is focused.
 * @param {string} value - The value of the cell.
 * @param {string} placeholder - The placeholder text of the text field.
 * @param {function} onChange - The function to call when the text field changes.
 * @param {string} fieldType - The type of field to render.
 */
const CellEditableField: React.FC<EditableFieldProps> = ({
  hasFocus,
  value,
  valueFormat = 'h:mm a',
  defaultValue,
  placeholder,
  onChange,
  fieldType = 'text',
  align = 'center',
  onBlur,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [textValue, setTextValue] = useState<string | ''>(defaultValue ?? '');
  const [timeValue, setTimeValue] = useState<string | ''>(defaultValue ?? '');

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextValue(e.target.value);
    onChange?.(e.target.value);
  };

  const handleTimeChange = (value: DateTime) => {
    setTimeValue(value?.toFormat(valueFormat) ?? '');
    onChange?.(value);
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [hasFocus]);

  return (
    <StyledBox className={classes.container}>
      {fieldType === 'text' ? (
        <TextField
          className={classes.input}
          value={textValue}
          onChange={handleTextChange}
          placeholder={placeholder}
          ref={ref}
          variant="outlined"
          size="small"
          inputProps={{
            style: { textAlign: align },
          }}
          onBlur={onBlur}
        />
      ) : (
        <TimeField
          value={timeValue ? DateTime.fromFormat(timeValue, valueFormat) : null}
          onChange={(value: DateTime | null) => {
            if (value) {
              handleTimeChange(value);
            }
          }}
          format={valueFormat}
          inputProps={{
            style: { textAlign: align },
          }}
          onBlur={onBlur}
        />
      )}
    </StyledBox>
  );
};

export { CellEditableField };
